<template>
  <el-dialog
    v-model="visible"
    width="500px"
    @close="onClose"
    :show-close="false"
    align-center
    append-to-body
    destroy-on-close
    class="appointment-modal appointment-modal--edit"
  >
    <div class="appointment-modal-body">
      <button class="modal-close-btn" type="button" @click="onClose">×</button>

      <div class="appointment-header-visual">
        <div class="header-row">
          <div class="header-item header-item--title">
            <el-icon><Calendar /></el-icon>
            <span>{{ formatDate(appointment.date) }} ({{ formattedDate(appointment.date) }} {{ weekdayLabel }})</span>
          </div>
          <div class="header-item header-item--time">
            <el-icon><Clock /></el-icon>
            <span>{{ appointment.start_time }}-{{ computedEndTime }}</span>
          </div>
        </div>

        <div class="header-row">
          <div class="header-item">
            <el-icon><UserFilled /></el-icon>
            <span>{{ staffEmail }}</span>
          </div>
          <div class="header-item header-item--room">
            <el-icon><Folder /></el-icon>
            <span>{{ appointment.room_name }}</span>
          </div>
        </div>

        <div class="header-row header-row--accent">
          <div class="header-item header-item--accent">
            <el-icon><AlarmClock /></el-icon>
            <span>{{ form.duration || totalDuration }} мин</span>
          </div>
        </div>
      </div>

      <div v-if="!clientIsDeleted" class="client-info-card">
        <div class="client-phone" v-if="appointment.client_phone">
          <el-icon><Phone /></el-icon>
          <a :href="`tel:${appointment.client_phone}`" class="phone-link">{{ appointment.client_phone }}</a>
          <el-tooltip content="Скопировать" placement="top" popper-class="calendar-copy-tooltip">
            <el-button :icon="CopyDocument" text @click="copyPhone" class="copy-button" circle />
          </el-tooltip>
        </div>
        <div class="client-name" @click="showClientProfile" title="Показать профиль клиента">
          <el-icon><UserFilled /></el-icon>
          <span class="name-text">{{ appointment.client_name }}</span>
          <el-button
            :icon="Edit"
            text
            circle
            @click.stop="showClientProfile"
            class="edit-button"
            title="Редактировать клиента"
          />
        </div>
      </div>

      <el-form :model="form" label-width="110px" label-position="top" class="appointment-form">
        <el-form-item>
          <ServiceMultiSelect
            v-model="form.services"
            :services="services"
            :remote="true"
            :city-id="appointment?.city_id"
            :show-selected-table="true"
          />
        </el-form-item>

        <el-form-item label="Длительность">
          <div class="duration-control">
            <el-button class="duration-btn" @click="changeDuration(-15)">−</el-button>
            <div class="duration-value">
              <span class="duration-amount">{{ form.duration || 0 }}</span>
              <span class="duration-unit">мин.</span>
            </div>
            <el-button class="duration-btn" @click="changeDuration(15)">+</el-button>
          </div>
        </el-form-item>

        <el-form-item label="Комментарий">
          <el-input
            type="textarea"
            v-model="form.comment"
            placeholder="Сложный клиент"
            :autosize="{ minRows: 3, maxRows: 4 }"
          />
        </el-form-item>
      </el-form>

      <div class="modal-footer-actions">
        <el-button @click="onClose" class="btn-cancel">Отмена</el-button>
        <el-button type="primary" @click="save" :disabled="!canSave" class="btn-save">Обновить</el-button>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  AlarmClock,
  Calendar,
  Clock,
  Edit,
  Files,
  Folder,
  Phone,
  UserFilled
} from '@element-plus/icons-vue'
import ServiceMultiSelect from '@/components/util/calendar/ServiceMultiSelect.vue'
import { mapState } from 'vuex'
import store from '@/components/store/store'

export default {
  name: 'AppointmentEditModal',
  components: {
    AlarmClock,
    Calendar,
    Clock,
    Folder,
    Phone,
    ServiceMultiSelect,
    UserFilled
  },
  props: {
    appointment: Object,
    date: { type: String, required: true }
  },
  data() {
    return {
      visible: true,
      form: {},
      initialized: false
    }
  },
  watch: {
    appointment: {
      handler(val) {
        this.initialized = false
        this.form = JSON.parse(JSON.stringify(val || {}))

        this.$nextTick(() => {
          if (this.form.duration_time) {
            this.form.duration = this.form.duration_time
          } else if (!this.form.duration) {
            this.form.duration = this.totalDuration
          }
          this.initialized = true
        })
      },
      immediate: true
    },
    'form.services': {
      handler() {
        if (this.initialized) {
          this.form.duration = this.totalDuration
        }
      },
      immediate: true,
      deep: true
    }
  },
  computed: {
    ...mapState('services', ['services']),
    ...mapState('auth', ['user']),
    CopyDocument() {
      return Files
    },
    Edit() {
      return Edit
    },
    canSave() {
      return Array.isArray(this.form.services) &&
        this.form.services.length > 0 &&
        (this.form.client_id || this.form.client_name)
    },
    clientIsDeleted() {
      const name = (this.appointment.client_name || '').trim()
      const phone = (this.appointment.client_phone || '').trim()
      return !name && !phone
    },
    staffEmail() {
      return this.appointment.user_email || this.appointment.employee_email || this.user?.email || ''
    },
    weekdayLabel() {
      if (!this.date) return ''
      const d = new Date(this.date)
      if (Number.isNaN(d.getTime())) return ''
      const weekdays = ['ВС', 'ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ']
      return weekdays[d.getDay()]
    },
    totalDuration() {
      if (this.form && this.form.services && this.form.services.length) {
        return this.form.services.reduce((sum, s) => sum + (s.duration || 0), 0)
      }
      return 0
    },
    totalPrice() {
      if (this.form && this.form.services && this.form.services.length) {
        return this.form.services.reduce((sum, s) => sum + Number(s.price || 0), 0)
      }
      return 0
    },
    computedEndTime() {
      return this.addMinutes(this.appointment.start_time, this.form.duration || this.totalDuration)
    }
  },
  methods: {
    changeDuration(step) {
      const next = (this.form.duration || 0) + step
      this.form.duration = Math.max(0, next)
    },
    showClientProfile() {
      if (this.appointment.client_id) {
        this.$emit('show-client', this.appointment.client_id)
      }
    },
    copyPhone() {
      if (!this.appointment.client_phone) return
      navigator.clipboard.writeText(this.appointment.client_phone).then(() => {
        this.$message.success('Скопировано')
      }).catch(() => {
        this.$message.error('Не удалось скопировать')
      })
    },
    onClose() {
      this.$emit('close')
    },
    formattedDate(date) {
      const months = [
        'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
        'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'
      ]
      const [, month, day] = date.split('-')
      const dayStr = day.length === 1 ? `0${day}` : day
      const monthName = months[parseInt(month, 10) - 1]
      return `${dayStr} ${monthName}`
    },
    formatDate(date) {
      if (!date) return ''
      const parts = date.split('-')
      if (parts.length !== 3) return date
      return `${parts[2]}.${parts[1]}.${parts[0]}`
    },
    addMinutes(time, minutes) {
      if (!time || !minutes) return time
      let [h, m] = time.split(':').map(Number)
      m += minutes
      h += Math.floor(m / 60)
      m %= 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    save() {
      const payload = {
        ...this.form,
        duration_time: this.form.duration || this.totalDuration,
        end_time: this.computedEndTime,
        total_sum: this.totalPrice
      }
      store.dispatch('appointments/updateAppointment', payload)
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.client-name {
  cursor: pointer;
}

.phone-link {
  color: inherit;
  text-decoration: none;
}

.name-text {
  user-select: text;
}

.duration-value {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.duration-amount {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.duration-unit {
  font-size: 12px;
  font-weight: 700;
}

</style>
